import { useState } from 'react';
import Button from '../../../components/common/Button';
import { authService } from '@/utils/auth/authService';
import { Building2, CheckCircle2, User, Loader2 } from 'lucide-react';

// Define types based on API response structure
interface ClientInfo {
    uuid: string;
    name: string;
    email: string;
    phone: string;
    image: string | null;
    status: string;
}

interface RoleInfo {
    uuid: string;
    name: string;
    level: number;
}

interface UserInfo {
    uuid: string;
    name: string;
    email: string;
    role: RoleInfo;
}

interface ClientData {
    client: ClientInfo;
    user: UserInfo;
}

interface SwitchAccountProps {
    clientsList: ClientData[];
    currentClientUuid: string;
    userEmail: string;
}

const SwitchAccountClient = ({ clientsList, currentClientUuid, userEmail }: SwitchAccountProps) => {
    const [switchingAccountUuid, setSwitchingAccountUuid] = useState<string | null>(null);

    const handleSwitchAccount = async (clientData: ClientData) => {
        setSwitchingAccountUuid(clientData.client.uuid);

        try {
            // Use email for account switching
            const response = await authService.switchAccount(
                userEmail,
                clientData.client.uuid
            );

            console.log('✅ Account switch response:', response);

            // IMPORTANT: Use window.location.replace() to force full page reload
            // This ensures the new token is picked up from cookies and old cached data is cleared
            window.location.replace('/leads');

        } catch (error) {
            console.error('❌ Error switching account:', error);
            alert('Failed to switch account. Please try again.');
            setSwitchingAccountUuid(null);
        }
    };

    return (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {clientsList.map((clientData) => {
                    const isCurrentAccount = clientData.client.uuid === currentClientUuid;
                    const isActive = clientData.client.status === 'active';

                    return (
                        <div
                            key={clientData.client.uuid}
                            className={`relative rounded-lg border-2 p-6 transition-all ${
                                isCurrentAccount
                                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                                    : isActive
                                    ? 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                                    : 'border-gray-200 bg-gray-50 opacity-75'
                            }`}
                        >
                            {/* Current Account Badge */}
                            {isCurrentAccount && (
                                <div className="absolute -top-3 left-4 flex items-center gap-1.5 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                    Current Account
                                </div>
                            )}

                            {/* Status Badge */}
                            {!isActive && (
                                <div className="absolute -top-3 right-4 bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                    Inactive
                                </div>
                            )}

                            {/* Account Icon */}
                            <div className="flex items-start gap-4 mb-4">
                                <div className="h-16 w-16 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                                    <Building2 className="h-8 w-8 text-white" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <h3 className="font-bold text-lg text-gray-900 truncate">
                                        {clientData.client.name}
                                    </h3>
                                    <p className="text-sm text-gray-600 truncate">
                                        {clientData.client.email}
                                    </p>
                                </div>
                            </div>

                            {/* User Info */}
                            <div className="flex items-center gap-2 mb-4 p-3 bg-white rounded-md border border-gray-100">
                                <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {clientData.user.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {clientData.user.role.name}
                                    </p>
                                </div>
                            </div>

                            {/* Action Button */}
                            <div className="flex gap-2">
                                {isCurrentAccount ? (
                                    <div className="w-full text-center py-3 text-sm font-semibold text-blue-600 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                        ✓ Current Account
                                    </div>
                                ) : isActive ? (
                                    <Button
                                        btnStyle="brandBtn"
                                        onClick={() => handleSwitchAccount(clientData)}
                                        className="w-full flex items-center justify-center gap-2"
                                        disabled={switchingAccountUuid === clientData.client.uuid}
                                    >
                                        {switchingAccountUuid === clientData.client.uuid ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Switching...
                                            </>
                                        ) : (
                                            'Switch to this Account'
                                        )}
                                    </Button>
                                ) : (
                                    <div className="w-full text-center py-3 text-sm font-medium text-gray-500 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                        Account is inactive
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};

export default SwitchAccountClient;
